import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { setCurrentStep } from "../../checkoutSlice";
import { RootState } from "../../../../../app/store";
import { useUpdateCustomer } from "../../../cart/hooks/useUpdateCustomer";
import { useGetCheckoutDataQuery } from "../../api/checkoutApi";
import { useGetCartQuery } from "../../../cart/api/cartApi";
import { TransformedAddress } from "../../../cart/types/cartApiTypes";
import {
  createBillingSchema,
  BillingFormData,
} from "../../helpers/checkoutValidation";
import { useUnsavedChangesWarning } from "../../../../../common/hooks/useUnsavedChangesWarning";
import CheckoutDetailsForm from "../common/CheckoutDetailsForm/CheckoutDetailsForm";
import CheckoutStepLayout from "../common/CheckoutStepLayout";
import UseBillingForShippingToggle from "../common/UseBillingForShippingToggle";

/**
 * Billing Step Component
 */
const BillingStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // --- Data Fetching ---
  const { data: checkoutData } = useGetCheckoutDataQuery();
  const { data: cartData } = useGetCartQuery();

  const billingAddress =
    checkoutData?.billingAddress || ({} as TransformedAddress);

  // Default to true if loading, to be safe
  const needsShipping = cartData?.needsShipping ?? true;
  // Check if an appointment is required
  const requiresAppointment =
    checkoutData?.appointmentData?.requiresAppointment || false;

  // --- Global State ---
  const useBillingForShipping = useAppSelector(
    (state: RootState) => state.checkout.useBillingForShipping,
  );

  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  // --- Form Initialization ---
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm<BillingFormData>({
    resolver: zodResolver(createBillingSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      ...billingAddress,
      customerType: billingAddress.customerType || "individual",
      tckn: billingAddress.tckn || "",
      vkn: billingAddress.vkn || "",
      company: billingAddress.company || "",
      country: billingAddress.country || "",
    },
  });

  useUnsavedChangesWarning(isDirty, isSubmitting);

  // --- Server State Sync ---
  useEffect(() => {
    if (checkoutData?.billingAddress) {
      const ba = checkoutData.billingAddress;
      if (!isDirty) {
        reset({
          ...ba,
          customerType: ba.customerType || "individual",
          tckn: ba.tckn || "",
          vkn: ba.vkn || "",
          company: ba.company || "",
          country: ba.country || "",
        });
      }
    }
  }, [checkoutData, reset, isDirty]);

  // --- Submission Handler ---
  const onSubmit = async (data: BillingFormData) => {
    const isTurkey = data.country === "TR";
    const cleanTckn =
      isTurkey && data.customerType === "individual" ? data.tckn : "";
    const cleanVkn =
      isTurkey && data.customerType === "corporate" ? data.vkn : "";
    const cleanCompany = data.customerType === "corporate" ? data.company : "";

    const finalData = {
      first_name: data.firstName,
      last_name: data.lastName,
      company: cleanCompany,
      country: data.country,
      address_1: data.address1,
      address_2: data.address2,
      city: data.city,
      state: data.state,
      postcode: data.postcode,
      phone: data.phone,
      email: data.email,
    };

    const payload: any = {
      billing_address: finalData,
      extensions: {
        additional_billing_info: {
          customer_type: data.customerType,
          tckn: cleanTckn,
          vkn: cleanVkn,
        },
      },
    };

    // --- CRITICAL DATA SYNC LOGIC ---
    // If user selected "Use Billing for Shipping"
    // OR if shipping is not needed (Virtual Products),
    // we implicitly sync the billing address to shipping address.
    if (useBillingForShipping || !needsShipping) {
      payload.shipping_address = finalData;
    }

    const result = await handleUpdateCustomer(payload);

    if (result.success) {
      reset(data);

      // --- Smart Navigation ---
      if (needsShipping) {
        dispatch(setCurrentStep("shipping"));
      } else if (requiresAppointment) {
        // Skip shipping visually, but go to appointment step
        dispatch(setCurrentStep("appointment"));
      } else {
        // Skip shipping and appointment if neither are needed
        dispatch(setCurrentStep("payment"));
      }
    }
  };

  const onError = () => {
    toast.error(
      t(
        "feedback.cart.customer.missingFieldBackend",
        "Please fill in all required fields.",
      ),
    );
  };

  return (
    <CheckoutStepLayout
      title={t("billingStep.stepTitle", "Billing Address")}
      onSubmit={handleSubmit(onSubmit, onError)}
      isProcessing={isUpdatingCustomer}
      nextButtonText={t("checkoutForm.actions.continue", "Continue")}
      isNextDisabled={false}
      // Only show toggle if shipping is ACTUALLY required
      afterFields={needsShipping ? <UseBillingForShippingToggle /> : null}
      showPrevButton={false}
      showNextButton={true}
      nextButtonType="submit"
    >
      <CheckoutDetailsForm
        register={register}
        control={control}
        errors={errors}
        setValue={setValue}
        watch={watch}
      />
    </CheckoutStepLayout>
  );
};

export default BillingStep;
